import React from 'react';

function HomePage({ C, Link, useBreakpoint }) {
	const { isMobile, isTablet, isDesktop, px } = useBreakpoint();
	const compact = isMobile || isTablet;

	return (
		<div>
			{/* HERO */}
			<section
				style={{
					minHeight: isMobile ? 'auto' : '100vh',
					display: 'flex',
					flexDirection: 'column',
					justifyContent: isMobile ? 'flex-start' : 'center',
					padding: isMobile ? `96px 24px 56px` : `120px ${px} 80px`,
					position: 'relative',
					overflow: 'hidden',
				}}
			>
				{/* Atmospheric background */}
				<div
					style={{
						position: 'absolute',
						inset: 0,
						zIndex: 0,
						background: `
						radial-gradient(ellipse 70% 60% at 65% 45%, ${C.teal}14 0%, transparent 65%),
						radial-gradient(ellipse 50% 70% at 15% 75%, ${C.green}0A 0%, transparent 55%)
					`,
					}}
				/>

				{/* Vertical rule, desktop only */}
				{isDesktop && (
					<div
						style={{
							position: 'absolute',
							left: px,
							top: '15%',
							bottom: '15%',
							width: 1,
							background: `linear-gradient(to bottom, transparent, #1E3230, transparent)`,
						}}
					/>
				)}

				<div
					style={{
						position: 'relative',
						zIndex: 1,
						maxWidth: compact ? '100%' : 860,
					}}
				>
					{/* Updated: Center for the Uncounted in section label */}
					<div
						className='section-label fade-up'
						style={{ marginBottom: 20, fontSize: isMobile ? 14 : 18 }}
					>
						Center for the Uncounted
					</div>

					<h1
						className='display-heading fade-up delay-1'
						style={{
							fontSize: isMobile
								? 'clamp(54px,15vw,80px)'
								: compact
									? 'clamp(44px,10vw,68px)'
									: 'clamp(52px,6vw,88px)',
							marginBottom: 2,
							lineHeight: 1.05,
						}}
					>
						Census
					</h1>
					<h1
						className='display-heading fade-up delay-2'
						style={{
							fontSize: isMobile
								? 'clamp(54px,15vw,80px)'
								: compact
									? 'clamp(44px,10vw,68px)'
									: 'clamp(52px,6vw,88px)',
							marginBottom: 2,
							color: C.teal,
							lineHeight: 1.05,
						}}
					>
						Disruption
					</h1>
					<h1
						className='display-heading fade-up delay-3'
						style={{
							fontSize: isMobile
								? 'clamp(54px,15vw,80px)'
								: compact
									? 'clamp(44px,10vw,68px)'
									: 'clamp(52px,6vw,88px)',
							marginBottom: isMobile ? 20 : 32,
							lineHeight: 1.05,
						}}
					>
						Model
					</h1>

					<p
						className='fade-up delay-4'
						style={{
							fontSize: isMobile ? 24 : compact ? 16 : 18,
							color: '#8AACAA',
							maxWidth: 540,
							lineHeight: 1.85,
							marginBottom: isMobile ? 28 : 36,
						}}
					>
						A probabilistic framework for identifying individuals whose
						historical continuity was disrupted under conditions consistent with
						mass violence, displacement, and institutional erasure.
					</p>

					<div
						className='fade-up delay-5'
						style={{
							display: 'flex',
							flexDirection: isMobile ? 'column' : 'row',
							gap: 12,
						}}
					>
						<Link
							to='/projects'
							className='btn-primary'
							style={{
								padding: isMobile ? '16px 28px' : '14px 28px',
								fontSize: isMobile ? 14 : 14,
							}}
						>
							View Our Projects →
						</Link>
						<Link
							to='/whitepaper'
							className='btn-outline'
							style={{
								padding: isMobile ? '15px 27px' : '13px 27px',
								fontSize: isMobile ? 14 : 14,
							}}
						>
							Read the Whitepaper
						</Link>
					</div>

					{/* Stats, inline on mobile/tablet */}
					{compact && (
						<div
							style={{
								display: 'flex',
								gap: isMobile ? 32 : 40,
								marginTop: isMobile ? 32 : 48,
								flexWrap: 'wrap',
							}}
						>
							{[
								{ n: '1.7M+', l: 'Records' },
								{ n: '1,739', l: 'Risk pool' },
								{ n: '878', l: 'Households' },
							].map(({ n, l }) => (
								<div key={n}>
									<div
										className='stat-number'
										style={{ fontSize: isMobile ? 38 : 32 }}
									>
										{n}
									</div>
									<div
										style={{
											fontSize: 14,
											fontFamily: 'DM Mono',
											color: '#4A6A66',
											textTransform: 'uppercase',
											letterSpacing: '0.1em',
											marginTop: 4,
										}}
									>
										{l}
									</div>
								</div>
							))}
						</div>
					)}
				</div>

				{/* Stats, floated right on desktop only */}
				{isDesktop && (
					<div
						style={{
							position: 'absolute',
							right: px,
							top: '50%',
							transform: 'translateY(-50%)',
							textAlign: 'right',
						}}
					>
						{[
							{ n: '1.7M+', l: 'Records analyzed' },
							{ n: '1,739', l: 'In risk pool' },
							{ n: '878', l: 'Households mapped' },
						].map(({ n, l }) => (
							<div key={n} style={{ marginBottom: 36 }}>
								<div className='stat-number' style={{ fontSize: 42 }}>
									{n}
								</div>
								<div
									style={{
										fontSize: 14,
										fontFamily: 'DM Mono',
										letterSpacing: '0.1em',
										color: '#4A6A66',
										textTransform: 'uppercase',
									}}
								>
									{l}
								</div>
							</div>
						))}
					</div>
				)}

				{/* Scroll indicator, desktop only */}
				{isDesktop && (
					<div
						style={{
							position: 'absolute',
							bottom: 36,
							left: '50%',
							transform: 'translateX(-50%)',
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							gap: 8,
						}}
					>
						<div
							style={{
								fontFamily: 'DM Mono',
								fontSize: 14,
								letterSpacing: '0.2em',
								color: '#2A4A46',
								textTransform: 'uppercase',
							}}
						>
							Scroll
						</div>
						<div
							style={{
								width: 1,
								height: 36,
								background: `linear-gradient(to bottom, #1E3230, transparent)`,
								animation: 'pulse 2s infinite',
							}}
						/>
					</div>
				)}
			</section>

			{/* CONTEXT STRIP */}
			<section style={{ padding: `60px ${px}`, background: '#070F0D' }}>
				<div
					style={{
						display: 'grid',
						gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
						gap: 2,
					}}
				>
					{[
						{
							label: 'The Event',
							text: `On May 31–June 1, 1921, the Greenwood District of Tulsa, "Black Wall Street", was destroyed by a white mob with state assistance. Hundreds were killed. The official record was suppressed.`,
						},
						{
							label: 'The Gap',
							text: 'Official death certificates number in the dozens. Historians estimate 100–300 killed. A century later, no comprehensive individual accounting of those lost has been completed.',
						},
						{
							label: 'The Method',
							text: 'CDM uses IPUMS census microdata to identify prime-age Black Tulsans who disappear from the historical record after 1920, under conditions where disappearance is statistically anomalous.',
						},
					].map(({ label, text }) => (
						<div
							key={label}
							style={{
								background: '#111E1C',
								padding: compact ? '32px 24px' : '48px 40px',
							}}
						>
							<div
								style={{
									fontFamily: 'DM Mono',
									fontSize: 10,
									letterSpacing: '0.18em',
									textTransform: 'uppercase',
									color: C.tealLight,
									marginBottom: 14,
								}}
							>
								{label}
							</div>
							<p style={{ fontSize: 14, color: '#6A8A86', lineHeight: 1.9 }}>
								{text}
							</p>
						</div>
					))}
				</div>
			</section>

			{/* PRINCIPLE */}
			<section
				style={{ padding: `80px ${px}`, borderTop: '1px solid #1A2A28' }}
			>
				<div
					style={{
						display: 'grid',
						gridTemplateColumns: compact ? '1fr' : '1fr 1fr',
						gap: compact ? 48 : 80,
						alignItems: 'center',
					}}
				>
					<div>
						<div className='section-label' style={{ marginBottom: 20 }}>
							Guiding Principle
						</div>
						<blockquote
							style={{
								fontFamily: 'Playfair Display',
								fontStyle: 'italic',
								fontSize: compact
									? 'clamp(20px,5vw,30px)'
									: 'clamp(26px,3vw,40px)',
								lineHeight: 1.4,
								color: 'white',
								borderLeft: `3px solid ${C.teal}`,
								paddingLeft: 24,
							}}
						>
							"The data belongs to the community."
						</blockquote>
						<p
							style={{
								marginTop: 24,
								fontSize: 15,
								color: '#8AACAA',
								lineHeight: 1.9,
							}}
						>
							CDM exists to support, not replace, community memory, descendant
							knowledge, and established historical scholarship. Every finding
							is a research lead, not a conclusion.
						</p>
					</div>

					{/* Commitment cards */}
					<div
						style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}
					>
						{[
							{ n: 'No', d: 'names published from census microdata' },
							{
								n: 'No',
								d: 'assertion of victim status without corroboration',
							},
							{ n: 'Full', d: 'transparency in methodology and limitations' },
							{
								n: 'Open',
								d: 'to correction, critique, and community override',
							},
						].map(({ n, d }) => (
							<div
								key={d}
								style={{
									background: '#111E1C',
									border: '1px solid #1A2E2A',
									padding: compact ? '18px 14px' : '28px 24px',
								}}
							>
								<div
									style={{
										fontFamily: 'Playfair Display',
										fontSize: compact ? 24 : 30,
										color: C.tealLight,
										fontWeight: 600,
										marginBottom: 8,
									}}
								>
									{n}
								</div>
								<div
									style={{ fontSize: 13, color: '#6A8A86', lineHeight: 1.6 }}
								>
									{d}
								</div>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* FIVE FILTERS */}
			<section style={{ padding: `0 ${px} 80px` }}>
				<div style={{ borderTop: '1px solid #1A2A28', paddingTop: 60 }}>
					<div
						style={{
							display: 'flex',
							flexDirection: compact ? 'column' : 'row',
							justifyContent: 'space-between',
							alignItems: compact ? 'flex-start' : 'flex-end',
							gap: 20,
							marginBottom: 40,
						}}
					>
						<div>
							<div className='section-label' style={{ marginBottom: 14 }}>
								How It Works
							</div>
							<h2
								className='display-heading'
								style={{
									fontSize: compact
										? 'clamp(24px,6vw,36px)'
										: 'clamp(32px,3vw,46px)',
								}}
							>
								Five filters. One signal.
							</h2>
						</div>
						<Link to='/methodology' className='btn-outline'>
							Full Methodology →
						</Link>
					</div>

					<div
						style={{
							display: 'flex',
							flexDirection: compact ? 'column' : 'row',
							gap: compact ? 2 : 0,
						}}
					>
						{[
							{
								n: '01',
								label: 'Race',
								desc: 'Black individuals only, reflecting documented racial targeting',
							},
							{
								n: '02',
								label: 'Geography',
								desc: 'Oklahoma residents enumerated in 1920',
							},
							{
								n: '03',
								label: 'Urban Status',
								desc: 'Urban enumerations only, Greenwood was urban',
							},
							{
								n: '04',
								label: 'Age',
								desc: 'Prime-age cohorts 16–49, where failure is most anomalous',
							},
							{
								n: '05',
								label: 'Linkage',
								desc: 'No successful forward census link to 1930 or later',
							},
						].map(({ n, label, desc }, i) => (
							<div
								key={n}
								style={{
									flex: 1,
									padding: compact ? '20px' : '28px 24px',
									background: i % 2 === 0 ? '#111E1C' : '#0E1A18',
									border: '1px solid #1A2E2A',
									borderLeft: !compact && i > 0 ? 'none' : '1px solid #1A2E2A',
								}}
							>
								<div
									style={{
										fontFamily: 'DM Mono',
										fontSize: compact ? 22 : 28,
										color: `${C.paper}35`,
										fontWeight: 500,
										marginBottom: 10,
									}}
								>
									{n}
								</div>
								<div
									style={{
										fontWeight: 500,
										fontSize: 14,
										color: '#A0B0AE',
										marginBottom: 6,
									}}
								>
									{label}
								</div>
								<div
									style={{ fontSize: 13, color: '#4A6A66', lineHeight: 1.7 }}
								>
									{desc}
								</div>
							</div>
						))}
					</div>

					{/* Result bar */}
					<div
						style={{
							background: C.teal,
							padding: '18px 24px',
							display: 'flex',
							flexDirection: compact ? 'column' : 'row',
							gap: 12,
							justifyContent: 'space-between',
							alignItems: compact ? 'flex-start' : 'center',
						}}
					>
						<div
							style={{
								fontFamily: 'DM Mono',
								fontSize: 10,
								color: 'rgba(255,255,255,0.7)',
								letterSpacing: '0.1em',
								textTransform: 'uppercase',
							}}
						>
							Result
						</div>
						<div
							style={{
								fontFamily: 'Playfair Display',
								fontSize: compact ? 15 : 20,
								color: 'white',
							}}
						>
							~2,100 individuals in the risk pool, from over 1 million starting
							records
						</div>
					</div>
				</div>
			</section>

			{/* CTA */}
			<section
				style={{
					background: '#070F0D',
					padding: `60px ${px}`,
					display: 'flex',
					flexDirection: compact ? 'column' : 'row',
					justifyContent: 'space-between',
					alignItems: compact ? 'flex-start' : 'center',
					gap: 40,
				}}
			>
				<div>
					<div
						style={{
							fontFamily: 'DM Mono',
							fontSize: 10,
							letterSpacing: '0.18em',
							textTransform: 'uppercase',
							color: C.tealLight,
							marginBottom: 16,
						}}
					>
						Collaborate
					</div>
					<h2
						style={{
							fontFamily: 'Playfair Display',
							fontSize: compact
								? 'clamp(22px,5vw,34px)'
								: 'clamp(26px,3vw,42px)',
							color: 'white',
							maxWidth: 460,
							lineHeight: 1.3,
						}}
					>
						This work cannot be completed by a single researcher.
					</h2>
					{/* Updated: institutional attribution */}
					<p
						style={{
							marginTop: 12,
							fontFamily: 'DM Mono',
							fontSize: 11,
							letterSpacing: '0.12em',
							textTransform: 'uppercase',
							color: '#2A4A46',
						}}
					>
						A project of the Center for the Uncounted
					</p>
				</div>
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						gap: 14,
						alignItems: compact ? 'stretch' : 'flex-end',
					}}
				>
					<p
						style={{
							fontSize: 14,
							color: '#4A6A66',
							maxWidth: 360,
							textAlign: compact ? 'left' : 'right',
							lineHeight: 1.8,
						}}
					>
						Seeking partnerships with affected communities, archivists,
						historians, and institutions across every project.
					</p>
					{/* Updated: mailto link points to new institutional email */}
					<a
						href='mailto:thecenterfortheuncounted@censusdisruption.org'
						className='btn-primary'
						style={{ textDecoration: 'none' }}
					>
						Get Involved →
					</a>
				</div>
			</section>
		</div>
	);
}

export default HomePage;
